/**
 * WordPress dependencies
 */
import warn from '@safe-wordpress/warning';
import type { ConnectedProps } from '@wordpress/components/build-types/context/use-context-system';

/**
 * Internal dependencies
 */
import { useComponentsContext } from './context-system-provider';
import { getNamespace, getConnectedNamespace } from './utils';
import { getStyledClassNameFromKey } from './get-styled-class-name-from-key';
import { useCx } from '../utils/hooks/use-cx';

/**
 * @template TProps
 * @typedef {TProps & { className: string }} ConnectedProps
 */

/**
 * Custom hook that derives registered props from the Context system.
 * These derived props are then consolidated with incoming component props.
 *
 * @template {{ className?: string }} P
 * @param {P}      props     Incoming props from the component.
 * @param {string} namespace The namespace to register and to derive context props from.
 * @return {ConnectedProps<P>} The connected props.
 */
export function useContextSystem< P extends { className?: string } >(
	props: P,
	namespace: string
): ConnectedProps< P > {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const contextSystemProps: { [ key: string ]: any } = useComponentsContext();
	if ( typeof namespace === 'undefined' ) {
		warn( 'useContextSystem: Please provide a namespace' );
	}

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const contextProps = contextSystemProps?.[ namespace ] || {};

	/* eslint-disable jsdoc/no-undefined-types */
	/** @type {ConnectedProps<P>} */
	// @ts-expect-error We fill in the missing properties below
	const finalComponentProps: ConnectedProps< P > = {
		...getConnectedNamespace(),
		...getNamespace( namespace ),
	};
	/* eslint-enable jsdoc/no-undefined-types */

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const { _overrides: overrideProps, ...otherContextProps } = contextProps;

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument
	const initialMergedProps = Object.entries( otherContextProps ).length
		? Object.assign( {}, otherContextProps, props )
		: props;

	const cx = useCx();

	const classes = cx(
		getStyledClassNameFromKey( namespace ),
		props.className
	);

	// Provides the ability to customize the render of the component.
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
	const rendered =
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		typeof initialMergedProps.renderChildren === 'function'
			? // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
			  initialMergedProps.renderChildren( initialMergedProps )
			: // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
			  initialMergedProps.children;

	for ( const key in initialMergedProps ) {
		// @ts-expect-error filling in missing props
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
		finalComponentProps[ key ] = initialMergedProps[ key ];
	}

	for ( const key in overrideProps ) {
		// @ts-expect-error filling in missing props
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
		finalComponentProps[ key ] = overrideProps[ key ];
	}

	// Setting an `undefined` explicitly can cause unintended overwrites
	// when a `cloneElement()` is involved.
	if ( rendered !== undefined ) {
		// @ts-expect-error filling in missing props
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
		finalComponentProps.children = rendered;
	}

	finalComponentProps.className = classes;

	return finalComponentProps;
}
